'use client'
import { useEffect, useMemo, useState } from 'react'
import { Box, type StackProps, VStack } from '@chakra-ui/react'
import {
  Currency,
  type OrderAddressDTO,
  type OrderShippingAdditionalDataDTO,
  type PickupDTO
} from 'ecosystem'
import { commaSeparator, convertMinorUnitToValue, createMinorUnit } from 'shared-utils'
import { paymentShippingOptionsFactory, type PricedShippingOption } from 'api'
import { useUser } from 'auth'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import { useBuyerVat } from '../../../../common/hooks'
import { useCheckoutContext } from '../../../hooks'
import { pickupToOrderAddress } from './companyAddressToOrderAddress'
import { useShippingAddress } from './useShippingAddress'
import { DefaultPickupSelector } from './DefaultPickupSelector'
import {
  DeliveryMethodSelector,
  type DeliveryMethodOverrides,
  type DeliveryMode
} from './DeliveryMethodSelector'
import { ShippingMethodSelector } from './ShippingMethodSelector'
import { ShippingAddressPicker } from './ShippingAddressPicker'

export interface DefaultDeliveryCheckoutEventData {
  /** Canonical FE selection — ready to PUT to `additionalInfo.defaultDetails`. */
  defaultDetails: NonNullable<OrderShippingAdditionalDataDTO['defaultDetails']>
  /** Locally-computed shipping cost for the picked option. `0` for PICKUP. */
  shippingCost: number
  mode: DeliveryMode
  /** The option the shopper picked, resolved and priced. */
  selectedOption?: PricedShippingOption
  shippingAddress?: OrderAddressDTO
  selectedPickup?: PickupDTO
}

export interface DefaultDeliveryCheckoutEventMeta {
  /** True when the current selection is publishable. Wrappers use this to
   *  gate payment-widget interactions.
   *  - DELIVERY + `showShippingAddress=false` → always valid (the payment
   *    widget will collect the delivery address itself).
   *  - DELIVERY + `showShippingAddress=true` → requires a picked address.
   *  - PICKUP → requires a picked location. */
  isValid: boolean
  /** True once the cart has hydrated. Wrappers use this to suppress
   *  side effects until shipping cost is computable from a real total. */
  isCartReady: boolean
  modeChanged: boolean
  shippingAddressChanged: boolean
  selectedPickupChanged: boolean
  shippingCostChanged: boolean
  methodChanged: boolean
}

interface DefaultDeliveryCheckoutProps {
  /** Called whenever the FE selection changes. The parent decides what to
   *  do with it — typically debounced-PUT to the SF order and live-update
   *  the payment widget's cart. */
  onDataChange?: (
    data: DefaultDeliveryCheckoutEventData,
    meta: DefaultDeliveryCheckoutEventMeta
  ) => void
  /** Forwarded to the outer `VStack` — same hook `PostNordCheckout` uses so
   *  the payment-provider wrapper can constrain the section to match its
   *  widget's width (e.g. `{ maxW: '616px' }` for Svea). */
  containerProps?: StackProps
  /**
   * Render the shipping-address picker in DELIVERY mode.
   *
   * Default `false` — payment widgets (Svea, Nexi, …) collect the
   * delivery address themselves, so the FE picker is duplicate work for
   * most flows. Set to `true` from the wrapper when the FE actually owns
   * the address (e.g. an invoice flow with no payment widget, or a brand
   * that prefers picking from saved `companyProfile` SHIPPING addresses).
   *
   * PICKUP mode ignores this flag entirely — the pickup's address is
   * always surfaced as the canonical `shippingAddress`.
   */
  showShippingAddress?: boolean
  /**
   * Fields to drop from the shipping-address form when nothing downstream reads them.
   *
   * The DEFAULT branch books no carrier, so it needs no delivery contact — a flow that
   * already collects a required phone and name on the billing form would otherwise ask
   * for both twice. `reference` is a B2B invoice field with no consumer in a B2C flow.
   */
  shippingAddressFields?: { showReference?: boolean; showPhone?: boolean }
  /**
   * Recipient country to evaluate `countries` rules against when this component
   * does not own the address (`showShippingAddress=false`).
   *
   * Pass the payment session's purchase country for market-locked widgets —
   * a Svea or Nexi session is created per market and the shopper cannot enter a
   * delivery address outside it, so the configured country *is* the recipient's.
   * Omit it where the shopper can type a foreign address the widget never tells
   * us about (Klarna with `allow_separate_shipping_address`): there, leaving the
   * country unknown makes country rules fail closed and fall through to an
   * unconditional rate, which is the safe outcome.
   */
  deliveryCountryFallback?: string
}

/**
 * Self-contained delivery UI for the DEFAULT shipping branch (no third-
 * party carrier configured). Mirrors `PostNordCheckout` in shape: owns its
 * own mode / address / pickup state, exposes selection via `onDataChange`,
 * and is rendered inside every payment-provider wrapper that needs a
 * FE-driven delivery step (SveaDefault, future NexiDefault, LedyerDefault).
 *
 * - Reads `pickupOptions`, `summaryTotal`, `shippingConfig` from
 *   `CheckoutContext`.
 * - Computes the delivery shipping cost via `paymentShippingFactory` over
 *   `shippingConfig.simpleShipping` — pickup is always free.
 * - Address policy:
 *   - DELIVERY mode: the wrapper decides via `showShippingAddress`
 *     whether the FE owns address collection (`true`, typical for flows
 *     without a payment widget that captures address) or defers to the
 *     widget (`false`, default — Svea/Nexi/Qliro collect address inside
 *     their iframe).
 *   - PICKUP mode: the pickup's address is surfaced as the canonical
 *     `shippingAddress` so downstream consumers see "delivery to the
 *     pickup point". The shipping-address picker is never rendered in
 *     this mode.
 */
export const DefaultDeliveryCheckout = ({
  onDataChange,
  containerProps,
  showShippingAddress = false,
  shippingAddressFields,
  deliveryCountryFallback
}: DefaultDeliveryCheckoutProps) => {
  const { user } = useUser()
  const {
    pickupOptions,
    summaryTotal,
    netSummaryTotal,
    shippingConfig,
    orderResponse,
    paymentProviderConfig
  } = useCheckoutContext()
  const buyerVat = useBuyerVat(paymentProviderConfig)
  const { priceMaximumFractionDigits } = useUIGlobalsContext()
  const overrides = useOverridesContext<keyof DeliveryMethodOverrides>()
  // Shared with `DeliveryMethodSelector` so both selectors say "free" the same
  // way a brand has already configured.
  const freeShippingLabel = overrideText('Fri frakt', overrides?.invoiceDeliveryFreeLabel)

  const currency = orderResponse?.storefrontOrderResponse?.currency ?? Currency.SEK
  const formatCost = (n: number) => `${commaSeparator(n, priceMaximumFractionDigits)} ${currency}`

  const pickups = pickupOptions ?? []
  const pickupAvailable = pickups.length > 0

  // The store's own options, when it configures any. Their absence keeps the
  // legacy delivery/pickup toggle — including the copy brands override on it.
  const configuredMethods = shippingConfig?.defaultOptions?.methods
  const hasConfiguredMethods = Boolean(configuredMethods?.length)

  const [mode, setMode] = useState<DeliveryMode>('DELIVERY')
  const [methodId, setMethodId] = useState<string | undefined>(undefined)

  const { shippingAddress, setShippingAddress } = useShippingAddress({
    ownsAddress: showShippingAddress
  })

  const [selectedPickup, setSelectedPickup] = useState<PickupDTO | undefined>(undefined)

  // Cart-loaded gate — `summaryTotal` is 0 before Redux hydrates the cart
  // and `paymentShippingFactory` would otherwise misclassify the order as
  // below the free-shipping threshold.
  const isCartReady = summaryTotal > 0

  // The country the delivery rules are evaluated against.
  //
  // The picked address counts only when this component actually owns it. With
  // `showShippingAddress=false` the payment widget collects the delivery
  // address itself: `shippingAddress` is merely seeded from the company
  // profile, is never shown, never emitted (`effectiveShippingAddress` stays
  // undefined) and cannot be changed by the shopper — so pricing off it would
  // quote a country the payment session knows nothing about. The wrapper's
  // `deliveryCountryFallback` is the session's own country and wins there.
  const ruleCountryCode =
    (showShippingAddress ? shippingAddress?.country : undefined) ?? deliveryCountryFallback

  // Every option this buyer can pick right now. Recomputed whenever a rule
  // input moves — notably the shipping address the shopper picks below, which
  // is what makes country-conditioned options possible at all.
  const shippingOptions = useMemo(() => {
    if (!isCartReady) return []
    return paymentShippingOptionsFactory({
      orderAmount: createMinorUnit(summaryTotal),
      // Rates the merchant configured ex-VAT are evaluated against this.
      netOrderAmount: createMinorUnit(netSummaryTotal),
      locale: 'sv',
      opts: shippingConfig?.simpleShipping,
      shippingConfig,
      recipientCountryCode: ruleCountryCode,
      // The configured fee is VAT-inclusive at the store's rate. This value is
      // both displayed and persisted onto the order, so it has to be resolved
      // for the buyer exactly like the payment-session row is — otherwise the
      // order records one freight figure while the shopper is charged another.
      buyerVat,
      customerType: user?.customer?.type,
      pickupAvailable
    })
  }, [
    buyerVat,
    isCartReady,
    pickupAvailable,
    ruleCountryCode,
    shippingConfig,
    summaryTotal,
    netSummaryTotal,
    user?.customer?.type
  ])

  // A rule change can retire the option the shopper had selected (picking a
  // Danish address hides a Sweden-only express option). Reselecting keeps the
  // emitted cost and the rendered card in agreement.
  const selectedOption = useMemo(
    () =>
      shippingOptions.find((option) => option.id === methodId) ??
      shippingOptions.find((option) => option.preselected) ??
      shippingOptions[0],
    [methodId, shippingOptions]
  )

  useEffect(() => {
    if (selectedOption && selectedOption.id !== methodId) {
      setMethodId(selectedOption.id)
    }
  }, [methodId, selectedOption])

  const deliveryShippingCost = convertMinorUnitToValue(
    shippingOptions.find((option) => option.kind !== 'PICKUP')?.price ?? 0
  )

  // Configured options carry their own kind and price; the legacy toggle keeps
  // deciding pickup by mode and pricing it at zero.
  const effectiveMode: DeliveryMode = hasConfiguredMethods
    ? selectedOption?.kind === 'PICKUP'
      ? 'PICKUP'
      : 'DELIVERY'
    : mode
  const shippingCost = hasConfiguredMethods
    ? convertMinorUnitToValue(selectedOption?.price ?? 0)
    : effectiveMode === 'PICKUP'
      ? 0
      : deliveryShippingCost

  // A pickup option may restrict which of the store's locations it offers.
  const availablePickups = useMemo(() => {
    const allowedIds = selectedOption?.pickupIds
    if (!allowedIds?.length) return pickups

    const matched = pickups.filter((pickup) => allowedIds.includes(pickup.id))
    // A restriction that matches nothing is a stale or mistyped id, not an
    // instruction to offer no pickup at all — that would leave the shopper on
    // a pickup option they can never complete. Fall back to every location, the
    // same way `resolveDefaultShippingMethods` falls back when a rule hides
    // every delivery option.
    return matched.length ? matched : pickups
  }, [pickups, selectedOption?.pickupIds])

  // Preselect as soon as pickup is chosen — the store's default location, else
  // the first offered. Also re-runs when a method change retires the currently
  // selected location, so the emitted shipment never names one that is no
  // longer on offer.
  useEffect(() => {
    if (effectiveMode !== 'PICKUP') return
    if (selectedPickup && availablePickups.some((pickup) => pickup.id === selectedPickup.id)) return

    const next = availablePickups.find((pickup) => pickup.defaultPickup) ?? availablePickups[0]
    if (next) setSelectedPickup(next)
  }, [availablePickups, effectiveMode, selectedPickup])

  // Canonical shipping address surfaced to the consumer:
  // - PICKUP: the pickup's own address — "take pickup address as
  //   shipping address" per the spec.
  // - DELIVERY + showShippingAddress=true: the address the shopper picked.
  // - DELIVERY + showShippingAddress=false: undefined — the payment
  //   widget owns address collection on the BE side via its push handler.
  const effectiveShippingAddress = useMemo<OrderAddressDTO | undefined>(() => {
    if (effectiveMode === 'PICKUP')
      return selectedPickup ? pickupToOrderAddress(selectedPickup) : undefined
    return showShippingAddress ? shippingAddress : undefined
  }, [effectiveMode, selectedPickup, showShippingAddress, shippingAddress])

  const defaultDetails: NonNullable<OrderShippingAdditionalDataDTO['defaultDetails']> =
    useMemo(() => {
      // Only recorded when the store actually offers a choice — the implicit
      // single option adds nothing the order does not already say.
      const selectedMethod =
        hasConfiguredMethods && selectedOption
          ? {
              selectedMethod: {
                id: selectedOption.id,
                label: selectedOption.label,
                kind: selectedOption.kind,
                price: convertMinorUnitToValue(selectedOption.price)
              }
            }
          : {}

      if (effectiveMode === 'PICKUP') {
        return {
          isPickupFromStore: true,
          ...(selectedPickup ? { selectedPickup } : {}),
          ...(effectiveShippingAddress ? { shippingAddress: effectiveShippingAddress } : {}),
          ...selectedMethod
        }
      }
      return {
        isPickupFromStore: false,
        ...(effectiveShippingAddress ? { shippingAddress: effectiveShippingAddress } : {}),
        ...selectedMethod
      }
    }, [
      effectiveMode,
      hasConfiguredMethods,
      selectedOption,
      selectedPickup,
      effectiveShippingAddress
    ])

  // `showShippingAddress=false` in DELIVERY mode means "let the payment
  // widget collect" — always publishable. When the FE picker is on, it
  // requires the shopper to actually pick.
  const isValid =
    (effectiveMode === 'DELIVERY' && (!showShippingAddress || !!shippingAddress)) ||
    (effectiveMode === 'PICKUP' && !!selectedPickup)

  // Emit on every meaningful change. The meta flags let consumers cheaply
  // decide whether the payment-widget cart needs a rebuild (shipping cost
  // or mode flipped) vs. only a BE sync (address edits).
  const [prev, setPrev] = useState<{
    mode: DeliveryMode
    methodId?: string
    shippingAddress?: OrderAddressDTO
    selectedPickup?: PickupDTO
    shippingCost: number
  }>({ mode, shippingCost })

  useEffect(() => {
    const meta: DefaultDeliveryCheckoutEventMeta = {
      isValid,
      isCartReady,
      modeChanged: prev.mode !== effectiveMode,
      methodChanged: prev.methodId !== selectedOption?.id,
      shippingAddressChanged: prev.shippingAddress !== effectiveShippingAddress,
      selectedPickupChanged: prev.selectedPickup?.id !== selectedPickup?.id,
      shippingCostChanged: prev.shippingCost !== shippingCost
    }
    onDataChange?.(
      {
        defaultDetails,
        shippingCost,
        mode: effectiveMode,
        ...(selectedOption ? { selectedOption } : {}),
        shippingAddress: effectiveShippingAddress,
        selectedPickup
      },
      meta
    )
    setPrev({
      mode: effectiveMode,
      methodId: selectedOption?.id,
      shippingAddress: effectiveShippingAddress,
      selectedPickup,
      shippingCost
    })
    // `defaultDetails` is derived; depending on it would double-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mode/method/address/pickup/cost are the change signals
  }, [
    effectiveMode,
    selectedOption?.id,
    effectiveShippingAddress,
    selectedPickup,
    shippingCost,
    isValid,
    isCartReady
  ])

  return (
    <VStack align="stretch" gap={6} w="100%" {...containerProps}>
      {hasConfiguredMethods ? (
        <ShippingMethodSelector
          formatCost={formatCost}
          freeLabel={freeShippingLabel}
          onChange={setMethodId}
          options={shippingOptions}
          selectedId={selectedOption?.id}
        />
      ) : (
        <DeliveryMethodSelector
          deliveryCost={deliveryShippingCost}
          formatCost={formatCost}
          onChange={setMode}
          pickupAvailable={pickupAvailable}
          value={mode}
        />
      )}

      {/* Hidden rather than unmounted in PICKUP mode. The picker owns a react-hook-form
          instance and its own folded/expanded state, and unmounting throws both away —
          only an address the shopper explicitly saved survives a remount. Someone who
          types an address, glances at the pickup options and comes back would otherwise
          find the form blank. */}
      {showShippingAddress ? (
        <Box display={effectiveMode === 'DELIVERY' ? 'block' : 'none'} w="100%">
          <ShippingAddressPicker
            onChange={setShippingAddress}
            showPhone={shippingAddressFields?.showPhone}
            showReference={shippingAddressFields?.showReference}
            value={shippingAddress}
          />
        </Box>
      ) : null}

      {effectiveMode === 'PICKUP' ? (
        <DefaultPickupSelector
          onChange={setSelectedPickup}
          pickups={availablePickups}
          selectedId={selectedPickup?.id}
        />
      ) : null}
    </VStack>
  )
}
